import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";

const PILLARS = [
  {
    number: "01",
    title: "On-Demand Field CTO & Enterprise Validation",
    description:
      "Senior technical judgment in the moments that decide enterprise deals — selective, not standing coverage.",
    bullets: [
      "Senior presence in the room that directly addresses \u201clack of maturity\u201d objections from C-suite buyers.",
      "Steps into select, must-win Global 2000 pursuits to validate multi-cloud architectures and address security & compliance concerns.",
      "Clears specific technical blockages on named pursuits \u2014 not an on-call SE bench.",
    ],
    teaser: {
      value: "11% → 50%+",
      label: "live replication coverage expanded through phased, validated onboarding",
      slug: "replication-risk-scoring-preflight",
    },
    links: [
      { label: "Replication Readiness Scoring & Pre-Flight Engineering", slug: "replication-risk-scoring-preflight" },
      { label: "Financial Data Confidence & Governance", slug: "financial-data-confidence-governance-layer" },
    ],
    testId: "service-row-field-cto",
  },
  {
    number: "02",
    title: "Architectural-to-Asset GTM Engine",
    description:
      "Complex engineering converted into finite, reusable assets your team carries into every pursuit.",
    bullets: [
      "On-Camera Technical Ambassador \u2014 expert presenter for technical demos, 5\u201315 minute video screen-flows, and technical explainers (Agentic CDC, data conformity, multi-cloud ingestion).",
      "Content Pipeline Blueprinting \u2014 turns complex backend engineering into structured technical blueprints, whitepapers, and executive briefs.",
      "Vertical Translation \u2014 converts raw telemetry into domain-tailored value narratives for conservative sectors (Oil & Gas, Energy, Utilities, Federal).",
    ],
    teaser: {
      value: "75%",
      label: "faster AI deployment through governed data products",
      slug: "enterprise-data-product-factory-marketplace",
    },
    links: [
      { label: "Mainframe Data Liberation: Measured-Impact CDC", slug: "mainframe-data-liberation-mips-optimization" },
      { label: "Governed Clinical Data Streaming", slug: "healthcare-clinical-data-engine" },
      { label: "Telematics & Supply Chain Telemetry Engine", slug: "telematics-supply-chain-telemetry-engine" },
      { label: "SCADA Grid Reliability Anomaly Engine", slug: "scada-grid-reliability-anomaly-engine" },
    ],
    testId: "service-row-gtm-engine",
  },
  {
    number: "03",
    title: "Board-Level Governance & Product Alignment",
    description:
      "Product strategy tuned to what enterprise buyers actually fund \u2014 the highest-leverage counsel I practice.",
    bullets: [
      "High-Margin SKU Mapping \u2014 advises founders on refining AI and data roadmaps to map technical features to monetizable enterprise buyer demand.",
      "Selective Introductions \u2014 connects leadership with executive networks, strategic SI/cloud partners, and potential enterprise buyers where there is genuine fit.",
    ],
    teaser: {
      value: "54%",
      label: "faster time-to-market for governed data products",
      slug: "enterprise-identity-modernization-data-products",
    },
    links: [
      { label: "Identity Modernization & Governed Data Products", slug: "enterprise-identity-modernization-data-products" },
      { label: "AI-Driven Trade Promotion Optimization", slug: "ai-trade-promotion-optimization" },
      { label: "ESG Audit Lineage", slug: "esg-data-governance-medallion-audit-lineage" },
      { label: "Autonomous Agent Containment", slug: "autonomous-agent-containment" },
      { label: "Data Product Factory & Marketplace", slug: "enterprise-data-product-factory-marketplace" },
    ],
    testId: "service-row-governance",
  },
];

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="px-6 lg:px-12 py-24 lg:py-32"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
            02 — Capabilities
          </p>
          <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
            A Technical Grid for{" "}
            <span className="italic text-[#E6E4D9]">Enterprise Motion</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/60">
            Three capability pillars, practiced at critical moments — high
            leverage, not high hours.
          </p>
        </Reveal>

        <div className="mt-14 lg:mt-20 border-y border-white/10 divide-y divide-white/10">
          {PILLARS.map((pillar, i) => (
            <Reveal key={pillar.number} delay={i * 0.08} y={24}>
              <article
                data-testid={pillar.testId}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-16 lg:py-24 px-2 lg:px-4 hover:bg-white/[0.02] transition-colors duration-500"
              >
                <div className="lg:col-span-4">
                  <span className="font-mono text-xs text-[#E6E4D9] tracking-[0.3em] block mb-4">
                    {pillar.number}
                  </span>
                  <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-snug">
                    {pillar.title}
                  </h3>
                  <p className="mt-5 text-sm font-light leading-relaxed text-white/50">
                    {pillar.description}
                  </p>
                  <div className="mt-10 hidden lg:block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                      Linked Blueprints
                    </span>
                    <div className="border-t border-white/10 divide-y divide-white/10">
                      {pillar.links.map((link) => (
                        <Link
                          key={link.slug}
                          to={`/insights/${link.slug}`}
                          data-testid={`capability-link-${link.slug}`}
                          className="group/link flex items-center justify-between gap-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-[#E6E4D9] transition-colors duration-300"
                        >
                          {link.label}
                          <ArrowUpRight
                            size={12}
                            weight="bold"
                            className="shrink-0 opacity-0 -translate-x-1 transition-all duration-300 group-hover/link:opacity-100 group-hover/link:translate-x-0"
                          />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <ul className="space-y-6 max-w-3xl">
                    {pillar.bullets.map((bullet, j) => (
                      <li
                        key={j}
                        className="flex gap-4 text-base font-light leading-relaxed text-white/70"
                      >
                        <span className="mt-[0.65rem] h-px w-6 shrink-0 bg-[#E6E4D9]/50" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/insights/${pillar.teaser.slug}`}
                    data-testid={`capability-teaser-${pillar.number}`}
                    aria-label={`${pillar.teaser.value} — ${pillar.teaser.label}`}
                    className="group/teaser mt-12 block max-w-xl border border-white/10 bg-white/[0.02] px-6 lg:px-8 py-6 hover:border-[#E6E4D9]/30 hover:bg-white/[0.04] transition-colors duration-500"
                  >
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
                      See how this motion produced
                    </span>
                    <span className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                      <span className="font-serif italic text-3xl lg:text-4xl text-[#E6E4D9] whitespace-nowrap">
                        {pillar.teaser.value}
                      </span>
                      <span className="flex-1 text-sm font-light leading-relaxed text-white/60">
                        {pillar.teaser.label}
                      </span>
                      <ArrowUpRight
                        size={16}
                        weight="bold"
                        className="shrink-0 text-white/30 transition-all duration-300 group-hover/teaser:text-[#E6E4D9] group-hover/teaser:translate-x-0.5 group-hover/teaser:-translate-y-0.5"
                      />
                    </span>
                  </Link>
                  <div className="mt-10 lg:hidden">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 block mb-3">
                      Linked Blueprints
                    </span>
                    <div className="border-t border-white/10 divide-y divide-white/10">
                      {pillar.links.map((link) => (
                        <Link
                          key={link.slug}
                          to={`/insights/${link.slug}`}
                          data-testid={`capability-link-mobile-${link.slug}`}
                          className="flex items-center justify-between gap-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55"
                        >
                          {link.label}
                          <ArrowUpRight size={12} weight="bold" className="shrink-0" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="capabilities-how-we-work"
            className="mt-10 border border-white/10 bg-white/[0.02] px-6 lg:px-10 py-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-10"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] shrink-0">
              How we work
            </span>
            <p className="text-sm sm:text-base font-light leading-relaxed text-white/70">
              Sprint-based, light-touch, scarce calendar. Architecture, assets,
              and the must-win rooms. Their team executes.
            </p>
            <a
              href="#offerings"
              data-testid="capabilities-offerings-link"
              className="shrink-0 lg:ml-auto inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[#E6E4D9] transition-colors duration-300"
            >
              Engagement models
              <ArrowUpRight size={12} weight="bold" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
